import React, { useContext, useEffect, useId } from 'react';
import {
  Text,
  Divider,
  TextButton as TextButtonAlpha,
  Text as TextAlpha,
} from '@oneaudi/unified-web-alpha-components';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import styled from 'styled-components';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-alpha-common';
import { LinkWithCurrentPage, SUBMENUID, TRIGGERID } from '../../../../ContentType';
import { Context } from '../../../../context/Context';
import {
  LinkList,
  LinkListItem,
  LinkListLink,
  MobileMenuButtonContainer,
  MobileMenuContainer,
  MobileMenuWrapper,
  MobileMenuButtonWrapper,
} from '../../SubPageNavigationStyles';
import { useTrackingManager } from '../../../../utils/useTrackingManager';
import VtpButtons from '../../ui/VtpButtons';
import { attachVerticalNavigation } from '../../../../utils/a11y';
import { useI18n } from '../../../../i18n';

const StyledTextButtonAlpha = styled(TextAlpha)`
  color: ${({ theme }) => theme.colors.currentPage};
`;

const MobileMenu: React.FC = () => {
  const {
    content: { cta, links, secondaryCta, fascinationMode },
    hasCta,
    renderPrimaryButton,
    renderVtpButtons,
    renderSecondaryButton,
    submenuMobileRef,
    showSubmenu,
  } = useContext(Context);
  const secondaryButton = secondaryCta?.customCta;
  const { click } = useTrackingManager();
  const ariaSubmenu = useI18n('ariaSubmenu');
  const isMobile = useMediaQuery('(max-width: 767px)');

  const renderLink = (
    { text, href, currentPage, ariaLabel, opensInNewTab }: LinkWithCurrentPage,
    index: number,
  ) => {
    if (currentPage) {
      return (
        <LinkListItem key={useId()}>
          <StyledTextButtonAlpha
            role="presentation"
            variant={{ type: 'copy', order: '2', style: 'bold' }}
          >
            {text}
          </StyledTextButtonAlpha>
        </LinkListItem>
      );
    }
    return (
      <LinkListItem key={useId()}>
        <LinkListLink
          href={href}
          target={opensInNewTab ? '_blank' : '_self'}
          onClick={() => click('link', false, text, href, `${index + 1}`, '')}
          aria-current={currentPage ? 'page' : undefined}
          aria-label={ariaLabel || undefined}
          variant="standalone"
          size={isMobile ? 'sm' : 'md'}
        >
          <Text tag="span" variant={{ type: 'copy', order: '2', style: 'normal' }}>
            {text}
          </Text>
        </LinkListLink>
      </LinkListItem>
    );
  };

  useEffect(() => {
    if (!submenuMobileRef.current) return undefined;
    const cleanup = attachVerticalNavigation(submenuMobileRef.current);

    return () => cleanup();
  }, [submenuMobileRef]);

  return (
    <MobileMenuContainer fascinationMode={fascinationMode}>
      <MobileMenuWrapper
        ref={submenuMobileRef}
        className={showSubmenu ? 'show' : undefined}
        id={SUBMENUID.MOBILE}
        aria-label={ariaSubmenu}
        aria-labelledby={TRIGGERID.MOBILE}
      >
        {/* Link list / subpages */}
        {links && links.length > 0 && (
          <LinkList>{links.map((link, index) => renderLink(link, index))}</LinkList>
        )}
        {/* Cta section */}
        {hasCta && (
          <MobileMenuButtonWrapper>
            <MobileMenuButtonContainer>
              <Divider aria-hidden="true" margin={[0, 0, 'var(--spacing-relative-xl)', 0]} />
              {renderPrimaryButton && (
                <TextButtonAlpha
                  href={cta[0].href}
                  {...(cta[0]?.ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  target={cta[0].opensInNewTab ? '_blank' : '_self'}
                  variant="primary"
                  isFullWidth
                  size="sm"
                  onClick={() => click('cta', true, cta[0].text, cta[0].href, '', 'primary')}
                >
                  {cta[0].text}
                </TextButtonAlpha>
              )}
              {renderVtpButtons && <VtpButtons />}
              {renderSecondaryButton && (
                <TextButtonAlpha
                  href={secondaryButton.href}
                  {...(cta[0]?.ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  variant="secondary"
                  isFullWidth
                  size="sm"
                  onClick={() =>
                    click('cta', true, secondaryButton.text, secondaryButton.href, '', 'secondary')
                  }
                  margin={[renderPrimaryButton ? SYS_SPACE_FIXED_100 : 0, 0, 0, 0]}
                >
                  {secondaryButton.text}
                </TextButtonAlpha>
              )}
            </MobileMenuButtonContainer>
          </MobileMenuButtonWrapper>
        )}
      </MobileMenuWrapper>
    </MobileMenuContainer>
  );
};

export default MobileMenu;
